import { type RefObject, useCallback } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { Overline } from '@ev-tech/ui-core-components/Overline';
import { Button } from '@ev-tech/ui-core-components/Button';

import { useExposeModal } from '@/providers/ExposeModalProvider';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { useExposeTracking } from '@/hooks/useExposeTracking';
import { useExposePageProps } from '@/hooks/useExposePageProps';
import { useListing } from '@/hooks/queries/useListing';
import { WhatsAppIcon } from '@/components/common/Icons/WhatsAppIcon';
import { MailBoldIcon } from '@/components/common/Icons/MailBoldIcon';

import styles from './ContactButtonsGroup.module.css';

type ButtonGroupProps = {
  buttonGroupClassName?: string;
  overlineClassName?: string;
  buttonRef?: RefObject<HTMLButtonElement>;
  shouldShowStroke?: boolean;
  testId: string;
};

const REQUEST_DETAILS_OVERLINE_TRANSLATION_KEY = 'search.expose.requestDetailsVia';

const ContactFormTriggerButton = ({
  buttonText,
  testId,
  buttonRef,
}: {
  buttonText: string;
  testId: string;
  buttonRef?: RefObject<HTMLButtonElement>;
}) => {
  const { onContactModalTriggerClick } = useExposeModal();

  return (
    <Button
      ref={buttonRef ?? undefined}
      onClick={onContactModalTriggerClick}
      testId={testId}
      label={buttonText}
      icon={<MailBoldIcon />}
      size="medium"
    />
  );
};

export const ContactButtonsGroup = ({ buttonGroupClassName, overlineClassName, buttonRef, testId, shouldShowStroke }: ButtonGroupProps) => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { openWhatsAppModal, isWhatsAppModalOpen } = useExposeModal();
  const { trackExposeNavigationEvent } = useExposeTracking();

  const { featureFlags } = useExposePageProps();
  const {
    data: { genericListing },
  } = useListing();

  const shouldShowWhatsAppButton = featureFlags?.showWhatsAppButton && genericListing.shopWhatsappNumber;

  const handleOpenWhatsAppModal = useCallback(
    ({ isMobile }: { isMobile: boolean }) => {
      openWhatsAppModal(isMobile);
      trackExposeNavigationEvent({ elementId: `${testId}-whatsapp` });
    },
    [openWhatsAppModal, trackExposeNavigationEvent, testId],
  );

  if (!shouldShowWhatsAppButton)
    return (
      <div className={buttonGroupClassName}>
        <ContactFormTriggerButton buttonText={t('search.expose.shopCard.label.contactButton')} testId={testId} buttonRef={buttonRef} />
      </div>
    );

  return (
    <>
      <Overline text={t(REQUEST_DETAILS_OVERLINE_TRANSLATION_KEY)} className={overlineClassName} stroke={shouldShowStroke} />
      <div className={buttonGroupClassName}>
        <Button
          className={styles.desktopButton}
          disabled={isWhatsAppModalOpen}
          onClick={() => handleOpenWhatsAppModal({ isMobile: false })}
          variant="tertiary"
          label="WhatsApp"
          icon={<WhatsAppIcon fillColor="currentColor" />}
          size="medium"
        />
        <Button
          className={styles.mobileButton}
          disabled={isWhatsAppModalOpen}
          onClick={() => handleOpenWhatsAppModal({ isMobile: true })}
          variant="tertiary"
          label="WhatsApp"
          icon={<WhatsAppIcon fillColor="currentColor" />}
          size="medium"
        />
        <ContactFormTriggerButton buttonText={t('search.common.email')} testId={testId} buttonRef={buttonRef} />
      </div>
    </>
  );
};
